import React, { Component, useEffect } from "react";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { useNavigate, useLocation } from "react-router-dom";
import { map, mergeDeepRight, sortBy, or, invert, prepend } from "ramda";
import { useWebRouteBuilder } from "../../components-system/routing/use-routing.hook";
import { RootieComponent } from "../layout";
import { isNotEmpty, mapIndexed, sortByProp } from "ramda-adjunct";
import { reverse } from "ramda";
import { index } from "../../components-fabric/_defaults/screens/d_index";

export type TabItem1 = {
  id: string;
  title: string;
  components: React.ComponentType[];
  nestedTabs?: TabItem1[];
  path: string;
  index: number;
};

/**
 * Functional Component: NestedTabs
 *
 * This component renders a set of nested tabs. Each tab can contain nested tabs and components.
 *
 * Props:
 * @param {Object} props - The properties for the nested tabs.
 * @param {Array} props.tabs - An array of tab configurations.
 * @param {string} props.parentPath - The parent path for nested tabs.
 *
 * Returns:
 * @returns {JSX.Element} A nested tab component with each tab potentially containing more nested tabs and components.
 */
export const NestedTabs = ({
  tabs,
  parentPath,
}: {
  tabs: TabItem1[];
  parentPath: string;
}) => {
  const tabToSet = localStorage.getItem("tabToSet");
  const [currentIndex, setCurrentIndex] = React.useState(
    tabToSet !== null ? Number(tabToSet) : 0
  );

  useEffect(() => {
    localStorage.removeItem("tabToSet");
  }, []);

  const handleTabSelect = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <Tabs selectedIndex={currentIndex} onSelect={handleTabSelect}>
      <TabList>
        {tabs.map((tab) => (
          <Tab key={tab.id}>{tab.title}</Tab>
        ))}
      </TabList>
      {tabs.map((tab) => (
        <TabPanel key={tab.id}>
          {tab.nestedTabs && (
            <NestedTabs
              tabs={tab.nestedTabs}
              parentPath={`${parentPath}/${tab.path}`}
            />
          )}
          {tab.components.map((component) => (
            <RootieComponent component={component} />
          ))}
        </TabPanel>
      ))}
    </Tabs>
  );
};

/**
 * Flattens a nested tab structure into a single level array of tab configurations.
 *
 * @param {string} basePath - The base path for the tabs.
 * @param {Array} tabs - The array of tabs to flatten.
 * @param {Object} parentConfig - The configuration of the parent tab.
 * @returns {Array} - The flattened array of tab configurations.
 */
const flattenTabs = (
  basePath: string,
  tabs: any,
  parentConfig: {
    id: string;
    title: string;
    path: string;
    components: any[];
    tabs: any[];
  }
) => {
  const result = tabs.flatMap((tab: any) => {
    const { id, path, ...rest } = tab;
    const fullPath = `${basePath}/${
      isNotEmpty(tab.path) ? "/" + tab.path : ""
    }`;
    const tabConfig = mergeDeepRight(parentConfig, {
      id: parentConfig.id + "_" + tab.id,
      title: tab.title,
      path: fullPath,
      components: tab.components,
      nestedTabs: tab.nestedTabs,
      tabs: parentConfig.tabs,
      children: tab.children,
      ...rest,
    });

    if (tab.nestedTabs) {
      return [tabConfig, ...flattenTabs(fullPath, tab.nestedTabs, tabConfig)];
    }
    return [tabConfig];
  });
  return result;
};

/**
 * Flattens a configuration array, transforming nested tab structures into a flat array of configurations.
 *
 * @param {Array} config - The array of configurations to flatten.
 * @returns {Array} - The flattened array of configurations.
 */
export const flattenConfig = (config: any[]) => {
  const result = config.flatMap((elem: any, index) => {
    const { id, tabs, path, ...rest } = elem;
    const basePath = path;
    const pageConfig = {
      id: id,
      path: basePath,
      index,
      tabs: tabs.map((x: any) => ({
        ...x,
        index,
        path: basePath + "/" + x.path,
      })),
      nestedTabs: elem.nestedTabs || [],
      protected: false,
      tmp: "tabbed",
      ...rest,
    };
    return [...flattenTabs(basePath, elem.tabs, pageConfig)];
  });
  return result;
};
